import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGate, useUnit } from 'effector-react';
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';
import {
  ControlWrapper,
  getDefaultInputPropsAutocomplete,
  getAutocompleteOnChange,
  getAutocompleteValue,
  withControl,
} from '@unicorn/control';
import { RizersControlGate, rizersQuery } from './model';
import type { Rizer, Gate } from './types';

export const RizersControl = withControl<
  Partial<AutocompleteProps<Rizer, boolean, boolean, boolean>> & Gate
>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    helperText,
    multiple,
    clearable,
    placeholder,
    buildingIds = [],
    disableCloseOnSelect,
    ...props
  }) => {
    const { t } = useTranslation();
    const memoBuildingIds = useMemo(() => buildingIds, [buildingIds.join(',')]);

    useGate(RizersControlGate, { buildingIds: memoBuildingIds });
    const { data, pending } = useUnit(rizersQuery);

    const options = Array.isArray(data) ? data : [];

    const handleChange = (
      _: unknown,
      newValue: string | Rizer | (string | Rizer)[] | null
    ) => getAutocompleteOnChange(newValue, setValue, multiple);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <Autocomplete
          value={getAutocompleteValue(options, value, multiple)}
          onChange={handleChange}
          options={
            memoBuildingIds.length > 0
              ? memoBuildingIds.length > 1
                ? [{ id: 0, label: t('gate') }]
                : options
              : []
          }
          multiple={multiple}
          loading={pending}
          isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
          disableClearable={!clearable}
          disableCloseOnSelect={
            multiple ? (disableCloseOnSelect ?? true) : disableCloseOnSelect
          }
          renderInput={(params) => {
            const slotProps = {
              input: getDefaultInputPropsAutocomplete({
                params,
                readOnly,
                disabled,
                pending,
              }),
            };

            return (
              <TextField
                {...params}
                error={isError}
                placeholder={
                  multiple && Array.isArray(value) && value.length > 0 ? '' : placeholder
                }
                slotProps={slotProps}
              />
            );
          }}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);
